import { REACT_APP_API_URL_E_CONTRACT } from "constants/index";

import { useEffect, useState } from "react";

import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import {
  Button,
  ClockFastForwardIcon,
  FormContainerControlled,
  Mail01Icon,
  Typography,
} from "components";
import OTPInputHookForm from "components/OTPInput/OTPInput";
import { base, gray, primary } from "configs/colors";
import { shadows } from "configs/shadows";
import {
  useMailControllerRequestEmailOtp,
  useOtpControllerVerifyOtp,
} from "generated/ec";
import { emailRequired } from "helpers/yup";
import { LeftSection, PageContainer } from "pages/SignIn/styles";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";
import * as Yup from "yup";

import { EContractSignComponent } from "./EContractSignComponent";
import section from "../../auth/components/assets/images/section.png";
import {
  ContentImage,
  FormContainer,
  RightSection,
} from "../../auth/components/styles";

interface GetOTPInput {
  employeeId: string;
  eContractId: string;
}

interface LoginOTPInput {
  code: number;
}

const formGetOTP = Yup.object({
  email: emailRequired,
});

export const LoginComponent = () => {
  const [timeCount, setTimeCount] = useState<number>(0);
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [isLoginError, setIsLoginError] = useState<boolean>(false);
  const { id: idEContract } = useParams();
  const [query, setQuery] = useQueryParams({
    employeeId: StringParam,
  });
  const { employeeId } = query;

  const theme = useTheme();
  const mdUpMatched = useMediaQuery(theme.breakpoints.up("md"));

  const loginOTPMethods = useForm<any>({});
  const { watch: watchLoginOTP } = loginOTPMethods;

  // eslint-disable-next-line
  const { mutate: sendOTP } = useMailControllerRequestEmailOtp({
    axios: {
      baseURL: REACT_APP_API_URL_E_CONTRACT,
    },
    mutation: {
      onSuccess() {
        setTimeCount(30);
      },
    },
  });
  const handleSendOTP = (data: GetOTPInput) => {
    // eslint-disable-next-line
    sendOTP({
      data: {
        eContractId: data?.eContractId,
        employeeId: data?.employeeId,
      },
    });
  };

  // eslint-disable-next-line
  const { mutate: verifyOtp, isLoading: isLoadingVerify } =
    useOtpControllerVerifyOtp({
      axios: {
        baseURL: REACT_APP_API_URL_E_CONTRACT,
      },
      mutation: {
        onSuccess(data) {
          // eslint-disable-next-line
          // @ts-ignore
          if (data?.data) {
            setIsLogin(true);
          } else {
            setIsLoginError(true);
          }
        },
      },
    });
  const onSubmitOTP = (data: LoginOTPInput) => {
    // eslint-disable-next-line
    verifyOtp({
      data: {
        otp: watchLoginOTP("code"),
        eContractId: idEContract as string,
        employeeId: employeeId as string,
      },
    });
  };

  useEffect(() => {
    const myInterval = setInterval(() => {
      if (timeCount > 0) {
        setTimeCount(timeCount - 1);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  }, [timeCount, setTimeCount]);

  return isLogin ? (
    <EContractSignComponent />
  ) : (
    <PageContainer>
      <LeftSection mdUpMatched={mdUpMatched}>
        <Box
          margin={2}
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <FormContainer>
            <FormContainerControlled
              methods={loginOTPMethods}
              onSubmit={onSubmitOTP}
            >
              <Box display="flex" justifyContent="center" marginBottom="24px">
                <Box
                  display="flex"
                  borderRadius="8px"
                  justifyContent="center"
                  border={`1px solid ${gray[300]}`}
                  boxShadow={shadows.xs}
                  sx={{ background: base.White }}
                  padding="14px"
                  width="56px"
                  height="56px"
                >
                  <Mail01Icon />
                </Box>
              </Box>
              <Typography
                type="DisplaySM"
                fontWeight={600}
                color={gray[900]}
                align="center"
              >
                Check your email
              </Typography>
              <Typography
                type="TextMD"
                color={gray[600]}
                marginTop={3}
                marginY="12px"
                align="center"
              >
                We sent an One Time Password to
              </Typography>
              <Stack gap={6}>
                <OTPInputHookForm name="code" />
              </Stack>
              <Stack>
                <Button
                  size="lg"
                  useTypography
                  type="submit"
                  loading={
                    isLoadingVerify || loginOTPMethods.formState.isSubmitting
                  }
                  disabled={watchLoginOTP("code")?.length !== 6}
                  sx={{
                    ".MuiTypography-root": {
                      fontSize: "14px",
                      lineHeight: "20px",
                    },
                    padding: "10px 16px",
                  }}
                >
                  {isLoadingVerify || loginOTPMethods.formState.isSubmitting
                    ? "Loading ..."
                    : "Verify email"}
                </Button>
              </Stack>

              {isLoginError ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  marginY="32px"
                >
                  <Box>
                    <Typography type="TextSM" fontWeight={600} color="red">
                      Your OTP code is incorrect or expired! Please enter it
                      again.
                    </Typography>
                  </Box>
                </Stack>
              ) : null}

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                marginY="32px"
              >
                <Box>
                  {timeCount === 0 ? (
                    <Typography
                      type="TextSM"
                      fontWeight={600}
                      color={primary[700]}
                      onClick={() => {
                        handleSendOTP({
                          eContractId: idEContract as string,
                          employeeId: employeeId as string,
                        });
                      }}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      Click to send OTP
                    </Typography>
                  ) : (
                    <Box display="flex">
                      <Typography
                        type="TextSM"
                        fontWeight={600}
                        color={primary[400]}
                      >
                        &nbsp;
                        {`Click to resend after ${timeCount}s`}
                      </Typography>
                      <ClockFastForwardIcon
                        fontSize="small"
                        sx={{
                          color: primary[400],
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            </FormContainerControlled>
          </FormContainer>
        </Box>
        <Box padding={mdUpMatched ? 8 : 2}>
          <Typography
            type="TextSM"
            color={mdUpMatched ? gray[600] : primary[200]}
          >
            © Talentnet 2023
          </Typography>
        </Box>
      </LeftSection>
      {mdUpMatched ? (
        <RightSection>
          <ContentImage src={section} alt="Content" />
        </RightSection>
      ) : null}
    </PageContainer>
  );
};
